import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ArrowRightCircle,
  Check,
  FileText,
  Plus,
  Printer,
  Send,
  Trash2,
  XCircle,
} from 'lucide-react'
import { getCustomers, getProducts } from '../../api/master'
import {
  convertCustomerQuote,
  getCustomerQuote,
  saveCustomerQuote,
  sendCustomerQuote,
  voidCustomerQuote,
} from '../../api/sales'
import { getCompany } from '../../api/settings'
import ErrorBanner from '../../components/ErrorBanner'
import { HelpTip } from '../../components/HelpTip'
import Modal from '../../components/Modal'
import Button from '../../components/Button'
import Spinner from '../../components/Spinner'
import { useToast } from '../../components/Toast.jsx'
import { fmtDate, fmtDay, localToday, money, startOfToday } from '../../utils/format'
import { BackLink, DocHeader, StatusBadge } from '../../components/PageParts'
import DocumentPrint from '../../components/DocumentPrint'
import JobSelect from '../../components/JobSelect'
import { jobsOnly, useSession } from '../../session'
import { billQuoteStage } from '../../api/jobs'
import { CreateOption } from '../../components/CreateOption'
import { printPage, useAutoPrint } from '../../utils/print'

const todayIso = localToday
const moneyInput = (value) => Number(value || 0).toFixed(2)
const num = (value) => {
  const n = Number(String(value ?? '').replace(/,/g, ''))
  return Number.isFinite(n) ? n : 0
}
const round2 = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100

const addDays = (date, days) => {
  const [y, m, d] = String(date || '').slice(0, 10).split('-').map((part) => parseInt(part, 10))
  if (!y || !m || !d) return ''
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + Number(days || 0))
  return dt.toISOString().slice(0, 10)
}

const customerLabel = (customer) => {
  const name = `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim()
  return name || customer?.email || customer?.phone || `Customer #${customer?.customer_id}`
}

const uploadedAssetSrc = (path) => {
  const p = String(path || '').trim()
  if (!p) return ''
  if (/^(https?:)?\/\//i.test(p) || p.startsWith('data:')) return p
  return `${import.meta.env.VITE_API_URL || ''}${p}`
}

const preloadImage = (src) => new Promise((resolve) => {
  if (!src || typeof Image === 'undefined') {
    resolve()
    return
  }
  const img = new Image()
  let settled = false
  const done = () => {
    if (settled) return
    settled = true
    resolve()
  }
  img.onload = done
  img.onerror = done
  img.src = src
  setTimeout(done, 1200)
})

const isServiceItem = (product) => Number(product?.is_service || 0) === 1

const emptyLine = (lineType = 'Service') => ({
  product_id: '',
  item_name: '',
  description: '',
  quantity: '1',
  unit_price: '0.00',
  discount_amount: '0.00',
  tax_rate: '0',
  line_type: lineType,
})

const normalizeLine = (line) => ({
  product_id: line.product_id ? String(line.product_id) : '',
  item_name: line.item_name || line.product_name || '',
  description: line.description || '',
  quantity: String(line.quantity ?? line.qty ?? '1'),
  unit_price: String(line.unit_price ?? '0.00'),
  discount_amount: String(line.discount_amount ?? '0.00'),
  tax_rate: String(line.tax_rate ?? '0'),
  line_type: line.line_type === 'Product' ? 'Product' : 'Service',
})

export default function CustomerQuoteForm() {
  const { modules } = useSession()
  const jobOnly = jobsOnly(modules)
  const nav = useNavigate()
  const { quoteId } = useParams()
  // A new quote from a job's page arrives with ?job=&customer=.
  const [linkParams] = useSearchParams()
  const { notify } = useToast()
  const isNew = !quoteId

  const [customers, setCustomers] = useState([])
  const [products, setProducts] = useState([])
  const [company, setCompany] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [sending, setSending] = useState(false)
  const [converting, setConverting] = useState(false)
  // Billing in stages (progress invoices): how much is billed, and the stage form.
  const [billedPct, setBilledPct] = useState(0)
  const [stageOpen, setStageOpen] = useState(false)
  const [stagePct, setStagePct] = useState('30')
  const [stageRest, setStageRest] = useState(false)
  const [staging, setStaging] = useState(false)
  const [voiding, setVoiding] = useState(false)
  const [formError, setFormError] = useState('')
  const [voidOpen, setVoidOpen] = useState(false)
  const [voidReason, setVoidReason] = useState('')
  const [quote, setQuote] = useState({
    quote_id: null,
    quote_number: '',
    customer_id: linkParams.get('customer') || '',
    job_id: linkParams.get('job') || '',
    quote_date: todayIso(),
    valid_until: addDays(todayIso(), 30),
    memo: '',
    status: 'Draft',
    converted_invoice_id: null,
    converted_invoice_number: '',
  })
  const [lines, setLines] = useState([emptyLine()])

  const selectedCustomer = useMemo(
    () => customers.find((customer) => String(customer.customer_id) === String(quote.customer_id)),
    [customers, quote.customer_id]
  )
  const customerName = selectedCustomer ? customerLabel(selectedCustomer) : (quote.customer_id ? `Customer #${quote.customer_id}` : 'No customer selected')
  const statusLabel = quote.status || 'Draft'
  const statusLower = String(statusLabel || '').toLowerCase()
  const isDraft = statusLower === 'draft'
  const isSent = statusLower === 'sent'
  const readOnly = !isDraft
  const quoteNumber = quote.quote_number || (isNew ? 'New quote' : 'Draft quote')
  const isExpired = isSent && quote.valid_until && new Date(`${quote.valid_until}T00:00:00`) < startOfToday()
  const badge = isExpired ? { label: 'Expired', tone: 'warn' }
    : isSent ? { label: 'Waiting on customer', tone: 'info' }
    : statusLower === 'converted' ? { label: 'Invoiced', tone: 'good' }
    : statusLower === 'void' ? { label: 'Void', tone: 'bad' }
    : { label: 'Draft', tone: 'muted' }
  const companyName = company?.company_name || 'ASYS Retail'
  const companyLogoSrc = uploadedAssetSrc(company?.logo_path)
  const companyAddress = [company?.address].filter(Boolean)
  const companyContact = [company?.phone, company?.email, company?.website].filter(Boolean)
  const customerAddress = [
    selectedCustomer?.address,
    [selectedCustomer?.city, selectedCustomer?.state, selectedCustomer?.postal_code].filter(Boolean).join(', '),
    selectedCustomer?.country,
  ].filter(Boolean)
  const customerContact = [selectedCustomer?.phone, selectedCustomer?.email].filter(Boolean)

  const applyServerQuote = (data) => {
    const header = data?.header || {}
    setQuote({
      quote_id: header.quote_id || data?.quote_id || null,
      quote_number: header.quote_number || '',
      customer_id: header.customer_id ? String(header.customer_id) : '',
      job_id: header.job_id ? String(header.job_id) : '',
      quote_date: fmtDate(header.quote_date) || todayIso(),
      valid_until: fmtDate(header.valid_until) || '',
      memo: header.memo || '',
      status: header.status || 'Draft',
      converted_invoice_id: header.converted_invoice_id || null,
      converted_invoice_number: header.converted_invoice_number || '',
      total_amount: header.total_amount,
    })
    setBilledPct(Number(data?.billing?.billed_pct || 0))
    const nextLines = (data?.lines || []).map((line) => normalizeLine(line))
    setLines(nextLines.length ? nextLines : [emptyLine()])
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setFormError('')
      try {
        const [customerRows, productRows, companyRow] = await Promise.all([
          getCustomers(),
          getProducts({ limit: 500 }),
          getCompany().catch(() => null),
        ])
        if (cancelled) return
        setCustomers(Array.isArray(customerRows) ? customerRows : [])
        const productList = Array.isArray(productRows) ? productRows : []
        setProducts(productList)
        setCompany(companyRow || null)
        if (isNew) {
          // A company that keeps stock starts each line as a product.
          const firstType = productList.some((p) => !isServiceItem(p)) ? 'Product' : 'Service'
          setLines((prev) => prev.map((line) => (line.product_id || line.item_name ? line : { ...line, line_type: firstType })))
        } else {
          const data = await getCustomerQuote(quoteId)
          if (cancelled) return
          applyServerQuote(data)
        }
      } catch (e) {
        if (!cancelled) setFormError(e.message || 'Failed to load quote')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [quoteId, isNew])

  const updateQuote = (patch) => setQuote((prev) => ({ ...prev, ...patch }))
  const updateLine = (idx, patch) => setLines((prev) => prev.map((line, i) => (i === idx ? { ...line, ...patch } : line)))
  const stockItems = useMemo(() => products.filter((p) => !isServiceItem(p)), [products])
  const serviceItems = useMemo(() => products.filter(isServiceItem), [products])
  const addLine = () => setLines((prev) => [...prev, emptyLine(stockItems.length ? 'Product' : 'Service')])
  const removeLine = (idx) => {
    if (lines.length <= 1) {
      notify('A quote needs at least one item.')
      return
    }
    setLines((prev) => prev.filter((_, i) => i !== idx))
  }

  const lineCalc = (line) => {
    const gross = round2(num(line.quantity) * num(line.unit_price))
    const discount = Math.min(Math.max(round2(line.discount_amount), 0), gross)
    const net = round2(gross - discount)
    const tax = round2(net * (Math.max(0, num(line.tax_rate)) / 100))
    return { gross, discount, net, tax, total: round2(net + tax) }
  }

  const totals = useMemo(() => lines.reduce((acc, line) => {
    const calc = lineCalc(line)
    acc.subtotal += calc.gross
    acc.discount += calc.discount
    acc.tax += calc.tax
    acc.total += calc.total
    return acc
  }, { subtotal: 0, discount: 0, tax: 0, total: 0 }), [lines])

  const setLineType = (idx, lineType) => {
    const line = lines[idx]
    const product = products.find((row) => String(row.product_id) === String(line.product_id))
    const keepItem = product && (lineType === 'Service') === isServiceItem(product)
    updateLine(idx, { line_type: lineType, product_id: keepItem ? line.product_id : '' })
  }

  const selectProduct = (idx, productId) => {
    const product = products.find((row) => String(row.product_id) === String(productId))
    if (!product) {
      updateLine(idx, { product_id: '' })
      return
    }
    const taxRate = product.is_taxable === false || String(product.is_taxable) === '0' ? '0' : String(product.tax_rate ?? '0')
    updateLine(idx, {
      line_type: isServiceItem(product) ? 'Service' : 'Product',
      product_id: String(product.product_id),
      item_name: product.name || '',
      description: product.description || '',
      unit_price: moneyInput(product.unit_price),
      tax_rate: taxRate,
    })
  }

  const buildPayload = () => ({
    quote_id: quote.quote_id,
    quote_number: quote.quote_number,
    customer_id: quote.customer_id ? Number(quote.customer_id) : null,
    job_id: quote.job_id ? Number(quote.job_id) : null,
    quote_date: quote.quote_date,
    valid_until: quote.valid_until || null,
    memo: quote.memo,
    lines: lines.map((line) => ({
      product_id: line.product_id ? Number(line.product_id) : null,
      item_name: line.item_name,
      description: line.description,
      quantity: num(line.quantity),
      unit_price: num(line.unit_price),
      discount_amount: num(line.discount_amount),
      tax_rate: num(line.tax_rate),
      line_type: line.line_type === 'Product' ? 'Product' : 'Service',
    })),
  })

  const validate = () => {
    if (!quote.customer_id) return 'Choose a customer.'
    if (!quote.quote_date) return 'Enter the quote date.'
    if (!lines.length) return 'Add at least one item.'
    for (let i = 0; i < lines.length; i += 1) {
      const line = lines[i]
      if (!String(line.item_name || '').trim()) return `Item ${i + 1}: give it a name.`
      if (num(line.quantity) <= 0) return `Item ${i + 1}: the quantity must be more than zero.`
      if (num(line.unit_price) < 0) return `Item ${i + 1}: the price can't be negative.`
      if (num(line.discount_amount) < 0) return `Item ${i + 1}: the discount can't be negative.`
      if (num(line.tax_rate) < 0 || num(line.tax_rate) > 100) return `Item ${i + 1}: tax must be between 0 and 100%.`
      if (line.line_type === 'Product' && !line.product_id) return `Item ${i + 1}: pick the product, or make it a service.`
    }
    return ''
  }

  const saveDraft = async ({ silent = false } = {}) => {
    const err = validate()
    if (err) {
      setFormError(err)
      return null
    }
    setSaving(true)
    setFormError('')
    try {
      const saved = await saveCustomerQuote(buildPayload())
      applyServerQuote(saved)
      const savedId = saved?.quote_id || saved?.header?.quote_id
      if (!silent) notify('Draft saved')
      if (savedId && isNew) nav(`/sales/quotes/${savedId}`, { replace: true })
      return savedId || quote.quote_id
    } catch (e) {
      setFormError(e.message || 'Failed to save quote')
      return null
    } finally {
      setSaving(false)
    }
  }

  const sendQuote = async () => {
    const savedId = await saveDraft({ silent: true })
    if (!savedId) return
    setSending(true)
    setFormError('')
    try {
      await sendCustomerQuote(savedId)
      notify('Quote marked as sent')
      const refreshed = await getCustomerQuote(savedId)
      applyServerQuote(refreshed)
      nav(`/sales/quotes/${savedId}`, { replace: true })
    } catch (e) {
      setFormError(e.message || 'Failed to send quote')
    } finally {
      setSending(false)
    }
  }

  const convertToInvoice = async () => {
    if (!quote.quote_id) return
    setConverting(true)
    setFormError('')
    try {
      const result = await convertCustomerQuote(quote.quote_id)
      notify(result.invoice_number ? `Draft invoice ${result.invoice_number} created` : 'Draft invoice created')
      nav(`/sales/customer-invoices/${result.invoice_id}`)
    } catch (e) {
      setFormError(e.message || 'Failed to convert quote')
    } finally {
      setConverting(false)
    }
  }

  const leftPct = Math.max(0, Math.round((100 - billedPct) * 10) / 10)
  const byStages = billedPct > 0.05
  const canStage = !!quote.quote_id && (isSent || (statusLower === 'converted' && byStages)) && leftPct > 0.05
  const openStage = () => {
    setStageRest(leftPct <= 30.05)
    setStagePct(String(Math.min(30, leftPct)))
    setStageOpen(true)
  }
  const billStage = async () => {
    setStaging(true)
    setFormError('')
    try {
      const result = await billQuoteStage(quote.quote_id, stageRest ? { rest: true } : { percent: Number(stagePct) })
      notify(result.deposit_applied > 0 ? `Draft stage invoice created, with ${money(result.deposit_applied)} deposit taken off` : 'Draft stage invoice created')
      nav(`/sales/customer-invoices/${result.invoice_id}`)
    } catch (e) {
      setFormError(e.message || 'Could not bill the stage.')
      setStageOpen(false)
    } finally {
      setStaging(false)
    }
  }

  const submitVoidQuote = async () => {
    if (!quote.quote_id) return
    setVoiding(true)
    setFormError('')
    try {
      await voidCustomerQuote(quote.quote_id, { reason: voidReason })
      notify('Quote voided')
      setVoidOpen(false)
      setVoidReason('')
      const refreshed = await getCustomerQuote(quote.quote_id)
      applyServerQuote(refreshed)
    } catch (e) {
      setFormError(e.message || 'Failed to void quote')
    } finally {
      setVoiding(false)
    }
  }

  const printQuote = async () => {
    notify('Preparing the quote to print')
    if (typeof window === 'undefined') return
    await preloadImage(companyLogoSrc)
    const cleanup = () => document.body.classList.remove('ci-printing')
    document.body.classList.add('ci-printing')
    window.addEventListener('afterprint', cleanup, { once: true })
    setTimeout(() => {
      window.print()
      setTimeout(cleanup, 1200)
    }, 50)
  }
  useAutoPrint(!loading && !isNew, printQuote)


  return (
    <div className={`ci-form-page ${readOnly ? 'posted' : ''}`}>
      <DocHeader
        back={<BackLink to="/sales/quotes" label="Quotes" />}
        title={quoteNumber}
        badge={<StatusBadge label={badge.label} tone={badge.tone} />}
        meta={!loading && (
          <>
            {quote.customer_id ? customerName : 'No customer yet'}
            {quote.quote_date && ` · ${fmtDay(quote.quote_date)}`}
            {quote.converted_invoice_id && <> · <Link to={`/sales/customer-invoices/${quote.converted_invoice_id}`}>Became invoice {quote.converted_invoice_number || ''}</Link></>}
          </>
        )}
      />

      {formError && <ErrorBanner message={formError} />}
      {jobOnly && !quote.job_id && !readOnly && (
        <p className="doc-hint job-only-hint">Your plan bills from jobs: choose the job this quote is for below, or <Link to="/jobs">start from a job</Link>.</p>
      )}
      {loading ? (
        <Spinner label="Loading quote..." />
      ) : (
        <>
          <div className="ci-form-meta-card">
            <div className="ci-form-meta-top">
              <label className="ci-form-field">
                <span>Customer</span>
                <select disabled={readOnly} value={quote.customer_id} onChange={(e) => updateQuote({ customer_id: e.target.value })}>
                  <option value="">Choose a customer…</option>
                  <CreateOption kind="customer" />
                  {customers.map((customer) => (
                    <option key={customer.customer_id} value={customer.customer_id}>{customerLabel(customer)}</option>
                  ))}
                </select>
              </label>
              <JobSelect customerId={quote.customer_id} value={quote.job_id} disabled={readOnly} onChange={(v) => updateQuote({ job_id: v })} />
              <label className="ci-form-field">
                <span>Quote number</span>
                <input disabled={readOnly} value={quote.quote_number} onChange={(e) => updateQuote({ quote_number: e.target.value })} placeholder="Leave blank to number it for you" />
              </label>
              <label className="ci-form-field">
                <span>Quote date</span>
                <input disabled={readOnly} type="date" value={quote.quote_date} onChange={(e) => updateQuote({ quote_date: e.target.value })} />
              </label>
              <label className="ci-form-field">
                <span>Valid until</span>
                <input disabled={readOnly} type="date" value={quote.valid_until || ''} onChange={(e) => updateQuote({ valid_until: e.target.value })} />
              </label>
            </div>
            <div className="ci-form-meta-bottom">
              <label className="ci-form-field ci-correction-wide">
                <span>Note to customer</span>
                <textarea
                  className="ci-form-memo-textarea"
                  disabled={readOnly}
                  rows={2}
                  value={quote.memo}
                  onChange={(e) => updateQuote({ memo: e.target.value })}
                  placeholder={readOnly ? '' : 'Printed on the quote, e.g. Thank you for considering us.'}
                />
              </label>
            </div>
          </div>

          <div className="ci-form-lines-card">
            <div className="ci-form-lines-head">
              <div className="ci-form-lines-title">Items</div>
              <span>{lines.length} {lines.length === 1 ? 'item' : 'items'}</span>
            </div>
            <div className="ci-form-table-wrap">
              <table className="ci-form-line-table lines-cards">
                <thead>
                  <tr>
                    <th className="col-type"><span className="label-with-help">Type<HelpTip text="Product: something you keep in stock. It comes off stock when the quote becomes an invoice and is posted. Service: work you do, such as labour, delivery or repairs. Nothing comes off stock." /></span></th>
                    <th className="col-product">Item</th>
                    <th className="col-name">Item name</th>
                    <th>Description</th>
                    <th className="num col-qty">Qty</th>
                    <th className="num col-price">Price</th>
                    <th className="num col-discount">Discount</th>
                    <th className="num col-tax">Tax %</th>
                    <th className="num col-amount">Amount</th>
                    <th className="col-del"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, idx) => {
                    const calc = lineCalc(line)
                    const isProduct = line.line_type === 'Product'
                    const choices = isProduct ? stockItems : serviceItems
                    const current = line.product_id && !choices.some((p) => String(p.product_id) === String(line.product_id))
                      ? products.find((p) => String(p.product_id) === String(line.product_id)) : null
                    return (
                      <tr key={`${idx}-${line.product_id || 'custom'}`}>
                        <td data-label="Type">
                          <select className="ci-form-cell-select" disabled={readOnly} value={line.line_type} onChange={(e) => setLineType(idx, e.target.value)} aria-label={`Item ${idx + 1} type`}>
                            <option value="Product">Product</option>
                            <option value="Service">Service</option>
                          </select>
                        </td>
                        <td data-label="Item">
                          <select className="ci-form-cell-select" disabled={readOnly} value={line.product_id} onChange={(e) => selectProduct(idx, e.target.value)} aria-label={`Item ${idx + 1}`}>
                            <option value="">{readOnly ? '—' : isProduct ? 'Choose a product' : 'Type it in, or choose a service'}</option>
                            <CreateOption kind="product" />
                            {[...(current ? [current] : []), ...choices].map((product) => (
                              <option key={product.product_id} value={product.product_id}>
                                {product.sku ? `${product.sku} - ` : ''}{product.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td data-label="Item name"><input className="ci-form-cell-input" disabled={readOnly} value={line.item_name} onChange={(e) => updateLine(idx, { item_name: e.target.value })} placeholder={readOnly ? '' : 'Item name'} /></td>
                        <td data-label="Description"><input className="ci-form-cell-input" disabled={readOnly} value={line.description} onChange={(e) => updateLine(idx, { description: e.target.value })} placeholder={readOnly ? '' : 'Description'} /></td>
                        <td data-label="Qty" className="lc-half"><input className="ci-form-cell-input num" disabled={readOnly} value={line.quantity} onChange={(e) => updateLine(idx, { quantity: e.target.value })} /></td>
                        <td data-label="Price" className="lc-half"><input className="ci-form-cell-input num" disabled={readOnly} value={line.unit_price} onChange={(e) => updateLine(idx, { unit_price: e.target.value })} /></td>
                        <td data-label="Discount" className="lc-half"><input className="ci-form-cell-input num" disabled={readOnly} value={line.discount_amount} onChange={(e) => updateLine(idx, { discount_amount: e.target.value })} /></td>
                        <td data-label="Tax %" className="lc-half"><input className="ci-form-cell-input num" disabled={readOnly} value={line.tax_rate} onChange={(e) => updateLine(idx, { tax_rate: e.target.value })} /></td>
                        <td className="ci-form-cell-amount" data-label="Amount">{money(calc.total)}</td>
                        <td className="ci-form-del-cell">
                          {!readOnly && (
                            <button type="button" className="ci-form-del-btn" onClick={() => removeLine(idx)} aria-label="Remove line">
                              <Trash2 size={15} />
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {!readOnly && (
              <div className="ci-form-add-row">
                <button type="button" className="ci-form-add-line" onClick={addLine}>
                  <Plus size={14} />
                  Add item
                </button>
              </div>
            )}
          </div>

          <div className="ci-form-bottom-row">
            <div className="ci-form-hint">
              <p>A quote doesn’t touch your books or your stock. When the customer accepts, turn it into an invoice.</p>
            </div>
            <div className="ci-form-totals">
              <div className="ci-form-total-row">
                <span>Subtotal</span>
                <strong>{money(totals.subtotal)}</strong>
              </div>
              <div className="ci-form-total-row discount">
                <span>Discounts</span>
                <strong>{totals.discount > 0 ? `-${money(totals.discount)}` : '-'}</strong>
              </div>
              <div className="ci-form-total-row tax">
                <span>Tax</span>
                <strong>{totals.tax > 0 ? `${money(totals.tax)}` : '-'}</strong>
              </div>
              <div className="ci-form-total-divider" />
              <div className="ci-form-grand-total">
                <span>Total</span>
                <strong>{money(totals.total)}</strong>
              </div>
            </div>
          </div>

          <div className="ci-form-action-bar">
            <div className="ci-form-action-left">
              {isDraft && (
                <>
                  <button type="button" className="ci-btn ci-btn-primary" disabled={saving || sending} onClick={() => saveDraft()}>
                    <FileText size={14} />
                    {saving ? 'Saving…' : 'Save draft'}
                  </button>
                  <button type="button" className="ci-btn ci-btn-ghost" disabled={saving || sending} onClick={sendQuote}>
                    <Send size={14} />
                    {sending ? 'Saving…' : 'Mark as sent'}
                  </button>
                </>
              )}
              {isSent && !byStages && (
                <button type="button" className="ci-btn ci-btn-primary" disabled={converting} onClick={convertToInvoice}>
                  <ArrowRightCircle size={14} />
                  {converting ? 'Working…' : 'Make invoice'}
                </button>
              )}
              {canStage && (
                <button type="button" className={`ci-btn ${byStages ? 'ci-btn-primary' : 'ci-btn-ghost'}`} onClick={openStage}>
                  <ArrowRightCircle size={14} />
                  {byStages ? `Bill the next stage (${leftPct}% left)` : 'Bill in stages'}
                </button>
              )}
              <button type="button" className="ci-btn ci-btn-ghost" onClick={() => printPage(printQuote)}>
                <Printer size={14} />
                Print or save PDF
              </button>
              {statusLower === 'converted' && !byStages && (
                <span className="ci-form-posted-note">
                  This quote became invoice {quote.converted_invoice_number || ''}.
                </span>
              )}
              {byStages && (
                <span className="ci-form-posted-note">
                  Billed in stages: {billedPct}% of this quote so far.
                </span>
              )}
            </div>
            <div className="ci-form-action-right">
              {(isDraft || isSent) && (
                <button
                  type="button"
                  className="ci-form-danger"
                  disabled={voiding}
                  onClick={() => setVoidOpen(true)}
                >
                  <XCircle size={14} />
                  {voiding ? 'Voiding…' : 'Void quote'}
                </button>
              )}
            </div>
          </div>

          <Modal
            open={stageOpen}
            onClose={() => setStageOpen(false)}
            title={`Bill a stage of ${quoteNumber}`}
            subtitle={byStages ? `${billedPct}% is billed so far; ${leftPct}% is left.` : 'Invoice part of the quote now and the rest later, for example 30% to start, 40% halfway and the rest at the end.'}
            footer={
              <>
                <Button variant="ghost" onClick={() => setStageOpen(false)}>Cancel</Button>
                <Button onClick={billStage} loading={staging} disabled={!stageRest && !(Number(stagePct) > 0 && Number(stagePct) <= leftPct)}>
                  <ArrowRightCircle size={14} />
                  Make the stage invoice
                </Button>
              </>
            }
          >
            <label className="ci-form-toggle">
              <input type="checkbox" checked={stageRest} onChange={(e) => setStageRest(e.target.checked)} />
              Bill the rest ({leftPct}%) <HelpTip text="The last stage bills exactly what each line of the quote has left, so all the stages add up to the quote to the cent. Any paid deposit on the job is taken off it." />
            </label>
            {!stageRest && (
              <label className="ci-form-field">
                <span>Share of the quote to bill now (%)</span>
                <input inputMode="decimal" value={stagePct} onChange={(e) => setStagePct(e.target.value)} />
                <span className="cell-sub">That’s {money((Number(stagePct) || 0) / 100 * num(quote.total_amount))} of the quote’s {money(quote.total_amount)}, with each line billed at that share.</span>
              </label>
            )}
          </Modal>

          <Modal
            open={voidOpen}
            onClose={() => setVoidOpen(false)}
            title={`Void ${quoteNumber}`}
            subtitle="The quote stays in your records marked Void and can’t be sent or invoiced."
            footer={
              <>
                <Button variant="ghost" onClick={() => setVoidOpen(false)}>Cancel</Button>
                <Button variant="danger" onClick={submitVoidQuote} loading={voiding}>
                  <XCircle size={14} />
                  Void quote
                </Button>
              </>
            }
          >
            <label className="ci-form-field">
              <span>Why are you voiding it? (optional)</span>
              <input value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder="Why this quote is being voided" />
            </label>
          </Modal>

          <DocumentPrint
            company={{ name: companyName, tagline: company?.tagline, logo: companyLogoSrc, address: companyAddress, contact: companyContact }}
            title="Quote"
            number={quoteNumber}
            status={['Draft', 'Expired', 'Void', 'Invoiced'].includes(badge.label) ? badge : null}
            billTo={{ label: 'Prepared for', name: customerName, lines: [...customerAddress, ...customerContact] }}
            facts={[
              ['Quote date', fmtDay(quote.quote_date)],
              ['Valid until', fmtDay(quote.valid_until)],
            ]}
            highlight={{ label: 'Quote total', value: `${money(totals.total)}` }}
            lines={lines.map((line) => {
              const calc = lineCalc(line)
              return { name: line.item_name, description: line.description, qty: num(line.quantity), unitPrice: num(line.unit_price), discount: calc.discount, tax: calc.tax, amount: calc.total }
            })}
            totals={[
              ...(totals.discount > 0.004 || totals.tax > 0.004 ? [{ label: 'Subtotal', value: totals.subtotal }] : []),
              ...(totals.discount > 0.004 ? [{ label: 'Discounts', value: totals.discount, kind: 'minus' }] : []),
              ...(totals.tax > 0.004 ? [{ label: 'Tax', value: totals.tax }] : []),
              { label: 'Quote total', value: totals.total, kind: 'due' },
            ]}
            notes={quote.memo || 'Thank you for considering us.'}
            footer={`This is a quote, not an invoice.${quote.valid_until ? ` Prices are held until ${fmtDay(quote.valid_until)}.` : ''}${companyContact.length ? ` Questions? Contact ${companyContact[0]}.` : ''}`}
          />
        </>
      )}
    </div>
  )
}
